import {put, call} from 'redux-saga/effects';
import CaseType from "../../entities/caseType";
import * as Actions from "../actions/caseTypeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CaseTypeAction = {
    caseTypeId: string;
    caseType: CaseType;
    caseTypes: CaseType[];
};

export function* fetchOneCaseType(action: CaseTypeAction) {
	try {
		const caseType: CaseType = yield call(api.findOne, 'caseType', action.caseTypeId);

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_FETCH_ONE_SUCCESS, caseTypeId: action.caseTypeId, caseType});
	}
	catch(err) {
		yield put({type: Actions.CASETYPE_FETCH_ONE_FAILURE, caseTypeId: action.caseTypeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CaseTypeAction} action
 */
export function* saveCaseType(action: CaseTypeAction) {
    // console.debug('⮂ CASETYPE_SAVE saga', action.caseType?.id);
    
	if (action.caseType === undefined || action.caseType === null) {
		console.error('saveCaseType received a null');
		throw new Error('saveCaseType received a null');
	}

	if (!(action.caseType instanceof CaseType)) {
		action.caseType = new CaseType(action.caseType);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'caseType', action.caseType);
		if (addResult.isErr()) {
			console.error(`⮂ saveCaseType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPE_SAVE_FAILURE, caseType: action.caseType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_SAVE, caseType: action.caseType});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'caseType', action.caseType.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.CASETYPE_SAVE_FAILURE, caseType: action.caseType, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {CaseTypeAction} action
 * @param {CaseType[]} action.caseTypes
 */
export function* saveManyCaseTypes(action: CaseTypeAction) {
    // console.debug('⮂ CASETYPE_SAVE_MANY saga', action.caseTypes.map(caseType => caseType.id).join(','));
    
    if (!Array.isArray(action.caseTypes)) {
        console.error('saveManyCaseTypes expected an array, received', action.caseTypes);
        throw new Error('saveManyCaseTypes expected an array');
    }

	try {
		const toSave: CaseType[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.caseTypes.length; i++) {
			const caseType = action.caseTypes[i];
			caseType.persisted = false;
			toSave.push(caseType);
		}

		const addResult = yield call(idb.add, 'caseType', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyCaseTypes: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPE_SAVE_MANY_FAILURE, caseTypes: action.caseTypes, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_SAVE_MANY, caseTypes: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CASETYPE_SAVE_MANY_FAILURE, caseTypes: action.caseTypes, status: 500, err});

		console.error(`Error saving ${action.caseTypes.length} caseTypes to database`, err);
	}
}

/**
 * @param {CaseTypeAction} action
 */
export function* removeCaseType(action: CaseTypeAction) {
    const userId = getCurrentUserId();
	if (!userId || action.caseType === undefined || action.caseType === null || !(action.caseType instanceof CaseType)) {
			console.error('removeCaseType expected type CaseType, received', action.caseType);
			throw new Error('removeCaseType expected type CaseType');
		}

	try {
		// Mark as deleted
		action.caseType.status = 'D';
		action.caseType.addedOn = newHRTimestamp();
		action.caseType.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'caseType', action.caseType);
		if (addResult.isErr()) {
			console.error(`⮂ removeCaseType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPE_REMOVE_FAILURE, caseType: action.caseType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_REMOVE, caseType: action.caseType});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CASETYPE_REMOVE_FAILURE, caseType: action.caseType, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this CaseType.
	*
	* @param {CaseTypeAction} action
	*/
export function* restoreCaseType(action: CaseTypeAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCaseType expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.caseType === undefined || action.caseType === null) {
        console.error('restoreCaseType expected a CaseType, received null');
        throw new Error('restoreCaseType expected a CaseType, received null');
    }
    else if (!(action.caseType instanceof CaseType)) action.caseType = new CaseType(action.caseType); 
    
	try {
		// Mark as restored
		action.caseType.status = 'U';
		action.caseType.addedOn = newHRTimestamp();
		action.caseType.addedBy = userId;
		action.caseType.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'caseType', action.caseType);
		if (addResult.isErr()) {
			console.error(`⮂ restoreCaseType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CASETYPE_RESTORE_FAILURE, caseType: action.caseType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CASETYPE_RESTORE, caseType: action.caseType});
	}
	catch(err) {
		yield put({type: Actions.CASETYPE_RESTORE_FAILURE, caseType: action.caseType, status: 500, err});

		console.error('Error restoring from database', err);
	}
}